import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch } from 'Hooks/reduxHooks'
import useTranslationText from 'Hooks/useTranslationText'

import SubmitButton from 'Components/Buttons/SubmitButton'
import CancelButton from 'Components/Buttons/CancelButton'

import {
  GetExpertsInsuranceUnitById,
  getInsuranceExpertFieldsPagedFilter,
  getInsuranceUnitsPagedFilter,
  ReferDraftVisitByExpert,
} from 'Application/Visits/Actions/action'

import BottomSheetDropdown from 'Components/Form/Input/FDMDropDownInput'
import Icon from 'Components/Icon'

type SelectOption = {
  value: number
  label: string
}

interface FormValues {
  insuranceUnit: SelectOption | null
  expert: SelectOption | null
}

const RefrencePage = () => {
  const [translation] = useTranslationText()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const pathname = window.location.pathname
  const pathParts = pathname.split('/').filter(Boolean)
  const idNumber = Number(pathParts[pathParts.length - 1])

  const searchParams = new URLSearchParams(window.location.search)
  const tabValue = searchParams.get('tabValue')

  const { handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      insuranceUnit: null,
      expert: null,
    },
  })

  const selectedUnit = watch('insuranceUnit')
  const selectedExpert = watch('expert')

  const [insuranceUnits, setInsuranceUnits] = useState<SelectOption[]>([])
  const [experts, setExperts] = useState<SelectOption[]>([])
  const [expertHasBodyInsurance, setExpertHasBodyInsurance] = useState(true)

  const searchTimeout = useRef<any>(null)

  const searchInsuranceUnits = (inputValue: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)

    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await dispatch(
          getInsuranceUnitsPagedFilter({
            pageNumber: 1,
            pageSize: 20,
            UnitName: inputValue,
          }),
        )

        const mapped =
          response?.payload?.queryResult?.map((item: any) => ({
            value: item.id,
            label: `${item.unitCode} - ${item.unitTitle}`,
          })) ?? []

        setInsuranceUnits(mapped)
      } catch (error) {
        console.error(error)
      }
    }, 300)
  }

  useEffect(() => {
    if (!selectedUnit) {
      setExperts([])
      setValue('expert', null)
      return
    }

    const loadExperts = async () => {
      try {
        const response = await dispatch(GetExpertsInsuranceUnitById(selectedUnit.value))

        const mapped =
          response?.payload?.map((item: any) => ({
            value: item.id,
            label: item.fullName,
          })) ?? []

        setExperts(mapped)
        setValue('expert', null)
      } catch (error) {
        console.error(error)
      }
    }

    loadExperts()
  }, [selectedUnit, dispatch, setValue])

  useEffect(() => {
    if (!selectedExpert) return

    const checkExpert = async () => {
      try {
        const response = await dispatch(
          getInsuranceExpertFieldsPagedFilter({
            pageNumber: 1,
            pageSize: 1000,
            expertId: selectedExpert.value,
          }),
        )

        const fields = response?.payload?.queryResult ?? []

        const hasRequiredField = fields.some((field: any) => field.insuranceTypeId === 1220)

        setExpertHasBodyInsurance(hasRequiredField)
      } catch (error) {
        console.error(error)
        setExpertHasBodyInsurance(false)
      }
    }

    checkExpert()
  }, [selectedExpert, dispatch])

  const submitForm = async () => {
    try {
      const response = await dispatch(
        ReferDraftVisitByExpert({
          data: {
            DraftVisitId: idNumber,
            InsuranceUnitId: selectedUnit?.value,
            ExpertId: selectedExpert?.value,
          },
          t: translation('REFERE_ASSIGNED_SUCCSESS'),
          navigate,
        }),
      )
    } catch {}
  }

  return (
    <>
      <div className="flex justify-between items-center h-16 px-6 shrink-0">
        <div className="flex items-center gap-2">
          <div onClick={() => navigate(-1)}>
            <Icon name="arrow-right" className="w-6 h-6 text-gray-700" />
          </div>
          <div className="text-lg font-bold pl-2">{'ارجاع به کارشناس'}</div>
        </div>
      </div>

      <div className="content-inner mt-5 p-3 h-full">
        <div className="bg-white rounded shadow-sm p-4">
          <form onSubmit={handleSubmit(submitForm)}>
            <div className="grid grid-cols-1 gap-4">
              <BottomSheetDropdown
                label={translation('INSURANCE_UNIT_TITLE')}
                value={selectedUnit}
                onChange={(val: any) => setValue('insuranceUnit', val)}
                options={insuranceUnits}
                onInputChange={searchInsuranceUnits}
              />

              <BottomSheetDropdown
                label={translation('EXPERT')}
                value={selectedExpert}
                onChange={(val: any) => setValue('expert', val)}
                options={experts}
                disabled={!selectedUnit}
              />
            </div>

            <div className="border-t mt-4 pt-4 text-end">
              <CancelButton link={-1} />
              <button className="btn btn-primary btn-lg w-full">ارجاع</button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default RefrencePage
